'use client';

import { FC, useState } from 'react';
import { Content, isFilled } from '@prismicio/client';
import { PrismicRichText } from '@prismicio/react';
import { PrismicNextImage } from '@prismicio/next';
import { registerContact } from '@/actions/hubspot/registerContact';
import cookies from 'js-cookie';

const HUBSPOT_PORTAL_ID = '7119515';

// Field label to HubSpot internal name mapping.
// Keys are lowercased + trimmed labels (asterisks/colons stripped); values are
// the exact internal property names that HubSpot expects.
const FIELD_MAPPINGS: Record<string, string> = {
  // First name
  name: 'firstname',
  'first name': 'firstname',
  firstname: 'firstname',
  'full name': 'firstname',
  'your name': 'firstname',
  // Last name
  'last name': 'lastname',
  lastname: 'lastname',
  surname: 'lastname',
  // Email
  email: 'email',
  'email address': 'email',
  'your email': 'email',
  'work email': 'email',
  // Company
  company: 'company',
  'company name': 'company',
  'your company': 'company',
  organization: 'company',
  // Job title
  'job title': 'jobtitle',
  jobtitle: 'jobtitle',
  title: 'jobtitle',
  role: 'jobtitle',
  position: 'jobtitle',
  // Phone
  phone: 'phone',
  'phone number': 'phone',
  mobile: 'phone',
};

interface WithImagesProps {
  slice: Content.LeadCaptureHeroSlice;
}

/**
 * "With Images" variation for `LeadCaptureHero` — centered headline + form with
 * supporting images beneath. Extracted verbatim from the original slice so the
 * server dispatcher in `index.tsx` can render it as a client child.
 */
const WithImages: FC<WithImagesProps> = ({ slice }) => {
  const primary =
    slice.primary as Content.LeadCaptureHeroSliceWithImagesPrimary;
  const {
    background,
    headline,
    subheadline,
    description,
    form_title,
    form_fields,
    cta_button,
    resource_images,
    form_id,
  } = primary;
  const eyebrow = (primary as { eyebrow?: string }).eyebrow;
  const transparent = Boolean(
    (primary as { transparent_bg?: boolean }).transparent_bg,
  );

  const [formData, setFormData] = useState<Record<string, string>>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitSuccessful, setIsSubmitSuccessful] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleInputChange = (fieldType: string, value: string) => {
    setFormData((prev) => ({ ...prev, [fieldType.toLowerCase()]: value }));
    if (fieldErrors[fieldType.toLowerCase()]) {
      setFieldErrors((prev) => ({ ...prev, [fieldType.toLowerCase()]: '' }));
    }
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};
    form_fields.forEach((field) => {
      if (field.field_type) {
        const fieldType = field.field_type;
        const value = formData[fieldType.toLowerCase()] || '';
        if (!value.trim()) {
          errors[fieldType.toLowerCase()] = `${fieldType} is required`;
        }
      }
    });
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setErrorMessage(null);

    try {
      const hubspotProperties: Record<string, string> = {};
      Object.entries(formData).forEach(([key, value]) => {
        const normalizedKey = key
          .toLowerCase()
          .replace(/[*:]/g, '')
          .replace(/\s+/g, ' ')
          .trim();

        if (normalizedKey === 'full name' || normalizedKey === 'fullname') {
          const parts = value.trim().split(/\s+/);
          hubspotProperties['firstname'] = parts[0] || '';
          hubspotProperties['lastname'] = parts.slice(1).join(' ') || '';
        } else {
          const fieldName =
            FIELD_MAPPINGS[normalizedKey] ||
            normalizedKey.replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
          hubspotProperties[fieldName] = value;
        }
      });

      const { postSubmitAction } = await registerContact({
        properties: hubspotProperties,
        pageUri: typeof window !== 'undefined' ? window.location.href : '',
        pageName: typeof document !== 'undefined' ? document.title : '',
        hubspotPortalIdForm: HUBSPOT_PORTAL_ID,
        hubspotGuidForm: form_id || '',
        hubspotutkCookie: cookies.get('hubspotutk'),
      });

      if (postSubmitAction?.type === 'redirect' && postSubmitAction.value) {
        window.location.href = postSubmitAction.value;
        return;
      }

      setIsSubmitSuccessful(true);
      setFormData({});
    } catch (error) {
      console.error('Form submission error:', error);
      setErrorMessage(
        'There was an error submitting the form. Please try again.',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitSuccessful) {
    return (
      <section className="relative w-full overflow-hidden bg-[#041118] px-5 py-16 md:px-[100px] md:py-[80px]">
        {isFilled.image(background) && (
          <div className="absolute inset-0 z-0">
            <PrismicNextImage
              field={background}
              fill
              className="object-cover object-bottom"
              alt=""
            />
          </div>
        )}
        <div className="relative z-10 mx-auto flex min-h-[400px] max-w-[590px] flex-col items-center justify-center rounded-[16px] bg-[rgba(31,34,40,0.8)] p-10 text-center backdrop-blur-[48px]">
          <h2 className="font-inter text-[28px] font-bold leading-[1.2] text-white md:text-[32px]">
            Thank you!
          </h2>
          <p className="mt-4 font-inter text-[18px] leading-[1.5] text-white/80">
            Your submission has been received.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className={`relative flex w-full flex-col items-center justify-center gap-10 overflow-hidden px-5 py-16 md:px-[100px] md:py-[80px] ${
        transparent ? 'bg-transparent' : 'bg-[#041118]'
      }`}
    >
      {/* Background */}
      {!transparent && (
        <div className="absolute inset-0 z-0">
          {isFilled.image(background) && (
            <PrismicNextImage
              field={background}
              fill
              className="object-cover object-bottom"
              alt=""
            />
          )}
        </div>
      )}

      <div className="relative z-10 mx-auto flex w-full max-w-[1380px] flex-col items-center gap-10 md:gap-[60px]">
        <div className="flex w-full max-w-[946px] flex-col items-center gap-6 text-center md:gap-10">
          {/* Eyebrow pill */}
          {eyebrow && (
            <div className="rounded-full border border-[#00f557] px-5 py-2">
              <span className="font-inter text-[12px] font-normal uppercase tracking-[0.04em] leading-[1.2] text-white md:text-[14px]">
                {eyebrow}
              </span>
            </div>
          )}

          {/* Headline + Subheadline — joined as one continuous heading */}
          <div className="w-full">
            {/* Headline */}
            {isFilled.richText(headline) && (
              <div className="w-full font-inter text-[32px] font-extrabold leading-[1.2] md:text-[42px] lg:text-[52px]">
                <PrismicRichText
                  field={headline}
                  components={{
                    heading1: ({ children }) => (
                      <h1 className="m-0 text-white">{children}</h1>
                    ),
                  }}
                />
              </div>
            )}

            {/* Subheadline */}
            {isFilled.richText(subheadline) && (
              <div className="w-full font-inter text-[32px] font-extrabold leading-[1.2] text-[#00f557] md:text-[42px] lg:text-[52px]">
                <PrismicRichText
                  field={subheadline}
                  components={{
                    heading2: ({ children }) => (
                      <h2 className="m-0 text-[#00f557]">{children}</h2>
                    ),
                    strong: ({ children }) => (
                      <strong className="text-[#00f557]">{children}</strong>
                    ),
                  }}
                />
              </div>
            )}
          </div>

          {/* Description */}
          {isFilled.richText(description) && (
            <div className="w-full font-inter text-[16px] font-normal leading-[1.5] text-white/80 md:text-[18px]">
              <PrismicRichText
                field={description}
                components={{
                  paragraph: ({ children }) => (
                    <p className="mb-4 last:mb-0">{children}</p>
                  ),
                }}
              />
            </div>
          )}
        </div>

        {/* Form */}
        <div className="w-full max-w-[590px] rounded-[16px] bg-[rgba(31,34,40,0.8)] p-6 opacity-90 shadow-[0px_4px_65px_0px_rgba(0,0,0,0.08)] backdrop-blur-[48px] flex flex-col gap-6 items-center">
          {isFilled.keyText(form_title) && (
            <p className="font-inter text-[20px] font-medium leading-[1.2] text-white text-center">
              {form_title}
            </p>
          )}

          <form onSubmit={handleSubmit} className="flex flex-col gap-2 w-full">
            {form_fields.map((field) => {
              if (!field.field_type) {
                return null;
              }
              const fieldType = field.field_type;
              return (
                <div key={fieldType} className="flex flex-col gap-2 w-full">
                  <p className="text-[16px] font-normal leading-[1.5] text-white">
                    {field.field_label || fieldType} *
                  </p>
                  <input
                    type={
                      fieldType.toLowerCase() === 'email' ? 'email' : 'text'
                    }
                    placeholder={
                      field.placeholder ||
                      `Enter your ${fieldType.toLowerCase()}`
                    }
                    value={formData[fieldType.toLowerCase()] || ''}
                    onChange={(e) =>
                      handleInputChange(fieldType, e.target.value)
                    }
                    className="h-12 w-full rounded-lg bg-[rgba(67,68,68,0.4)] px-3 py-2 text-[16px] text-white placeholder-[rgba(255,255,255,0.3)] outline-none transition-colors focus:ring-1 focus:ring-[#00F557]"
                    required
                  />
                  {fieldErrors[fieldType.toLowerCase()] && (
                    <p className="text-red-400 text-sm">
                      {fieldErrors[fieldType.toLowerCase()]}
                    </p>
                  )}
                </div>
              );
            })}

            {/* CTA Button */}
            {isFilled.link(cta_button) && (
              <button
                type="submit"
                disabled={isSubmitting}
                className="mt-4 w-full rounded-full bg-[#00F557] px-6 py-3 font-manrope text-[16px] font-bold leading-[1.5] text-[#002726] transition-colors hover:bg-[#00D964] disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isSubmitting
                  ? 'Submitting...'
                  : cta_button.text || 'Get the Launch Framework'}
              </button>
            )}
          </form>

          {errorMessage && (
            <p className="text-red-400 text-sm text-center">{errorMessage}</p>
          )}
        </div>
      </div>

      {/* Resource Images */}
      {resource_images && resource_images.length > 0 && (
        <div className="relative z-10 w-full">
          {resource_images.map((item, index) => (
            <div
              key={index}
              className="flex flex-col gap-4 md:flex-row md:gap-4"
            >
              {isFilled.image(item.image_1) && (
                <div className="flex-1 overflow-hidden rounded-[16px]">
                  <PrismicNextImage
                    field={item.image_1}
                    className="h-auto w-full rounded-[16px] object-cover"
                    alt=""
                  />
                </div>
              )}
              {isFilled.image(item.image_2) && (
                <div className="flex-1 overflow-hidden rounded-[16px]">
                  <PrismicNextImage
                    field={item.image_2}
                    className="h-auto w-full rounded-[16px] object-cover"
                    alt=""
                  />
                </div>
              )}
              {isFilled.image(item.image_3) && (
                <div className="flex-1 overflow-hidden rounded-[16px]">
                  <PrismicNextImage
                    field={item.image_3}
                    className="h-auto w-full rounded-[16px] object-cover"
                    alt=""
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export default WithImages;
